import React, { useEffect, useState } from "react";
import biosenseAPI from "../../../../api/biosense";
import {
  Box,
  Grid,
  Card as ProtoCard,
  CardContent,
  Divider,
  List,
  ListItem as ProtoListItem,
  ListItemText,
  Avatar,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
} from "@mui/material";
import { Cancel, Save, Edit } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import base64ToImg from "../../../../utils/base64ToImg";
import SwalMediaImageUpload from "../../../components/swalMediaImageUpload";
import dayjs from "dayjs";
import useConfirmDialog from "../../../../utils/useConfirmDialogue";
import useFeedbackDialog from "../../../../utils/useFeedbackDialogue";
import _ from "lodash";
import { toTitleCase } from "../../../../utils/toTitleCase";
import { camelCaseToWords } from "../../../../utils/camelCaseToWords";

const ProjectDetails = ({ projectId }) => {
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [editMode, setEditMode] = useState({ field: null, value: null });
  const { t } = useTranslation();
  const confirmAction = useConfirmDialog();
  const { showSuccess, showError } = useFeedbackDialog();

  // Load project data
  useEffect(() => {
    const fetchProjectDetails = async () => {
      try {
        const response = await biosenseAPI.getProjectDetails({ id: projectId });
        setProject(response.project);
      } catch (error) {
        showError("Load Failed", error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProjectDetails();
  }, []);

  // Handle edit mode toggle
  const handleEditToggle = (fieldName) => {
    if (editMode.field === fieldName) {
      // Check for changes
      if (_.isEqual(editMode.value, project[fieldName])) {
        setEditMode({ field: null, value: null });
        return;
      }

      // Confirm save changes
      confirmAction(
        {
          title: "Save Changes?",
          text: `Are you sure you want to save changes to ${camelCaseToWords(fieldName)}?`,
          icon: "question",
        },
        () => saveFieldChange(fieldName, editMode.value),
      );
    } else {
      // Enter edit mode
      setEditMode({ field: fieldName, value: project[fieldName] });
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    if (_.isEqual(editMode.value, project[editMode.field])) {
      setEditMode({ field: null, value: null });
      return;
    }

    confirmAction(
      {
        title: "Discard Changes?",
        text: "Your changes will not be saved.",
        icon: "warning",
      },
      () => setEditMode({ field: null, value: null }),
    );
  };

  // Save field changes
  const saveFieldChange = async (fieldName, value) => {
    try {
      // Other fields update directly
      await biosenseAPI.updateProjectField({
        id: project.id,
        field: fieldName,
        value: value,
      });

      // Refresh project data after successful update
      const response = await biosenseAPI.getProjectDetails({ id: project.id });
      setProject(response.project);
      showSuccess("Update Successful", `${camelCaseToWords(fieldName)} has been updated`);

      // Reset edit state
      setEditMode({ field: null, value: null });
    } catch (error) {
      showError("Update Failed", error.message);
    }
  };

  const handleImageUpload = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target.result;
      };

      img.onload = async () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const base64Data = canvas.toDataURL("image/png");
        const base64Image = base64Data.split(",")[1];

        try {
          // 调用API上传Base64数据
          await biosenseAPI.updateProjectImage({ base64Image, id: project.id });

          // 更新项目详情
          const response = await biosenseAPI.getProjectDetails({ id: project.id });
          setProject(response.project);

          resolve(() => {
            return true;
          });
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      reader.readAsDataURL(file);
    });
  };

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ height: "100%", p: 1 }}>
        {/* 使用CSS Grid实现等高布局 */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 1,
            height: "100%",
            // md断点以上使用两列布局
            "@media (min-width: 900px)": {
              gridTemplateColumns: "5fr 7fr",
            },
          }}
        >
          {/* 左侧卡片 - 项目信息 */}
          <Box sx={{ height: "100%" }}>
            <ProtoCard sx={{ height: "100%", boxShadow: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
                  <Avatar sx={{ width: 80, height: 80 }}>
                    <SwalMediaImageUpload
                      props={{
                        component: "img",
                        isWithBlur: false,
                        title: "(沒有名字)",
                        height: "150",
                        width: "150",
                        style: {
                          objectFit: "cover",
                          objectPosition: "0 -50px",
                          borderRadius: "4px 4px 0 0",
                        },
                        src: "",
                        alt: "Card Background",
                      }}
                    />
                  </Avatar>
                  <Typography variant='h6' gutterBottom>
                    <CircularProgress sx={{ mt: 1 }} />
                  </Typography>
                  <Typography variant='body2' color='text.secondary' gutterBottom>
                    ID: <CircularProgress sx={{ mt: 1 }} />
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <List sx={{ p: 0 }}>
                  {[t("status"), t("createdat"), t("updatedat")].map((fieldName) => (
                    <ProtoListItem key={fieldName} sx={{ py: 1 }}>
                      <ListItemText
                        primary={fieldName}
                        secondary={<CircularProgress sx={{ mt: 1 }} />}
                      />
                    </ProtoListItem>
                  ))}
                </List>
              </CardContent>
            </ProtoCard>
          </Box>

          {/* 右侧卡片 - 项目详细信息 */}
          <Box sx={{ height: "100%" }}>
            <ProtoCard sx={{ height: "100%", boxShadow: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant={"h6"} sx={{ mb: 1 }}>
                  {t("projectinformation")}
                </Typography>
                <List sx={{ p: 0 }}>
                  {["alias", "lat", "lng"].map((fieldName) => (
                    <ProtoListItem key={fieldName} sx={{ py: 1 }}>
                      <Grid container alignItems='center'>
                        <Grid item xs={8}>
                          <ListItemText
                            sx={{ width: { lg: "14em" } }}
                            primary={toTitleCase(camelCaseToWords(fieldName))}
                            secondary={<CircularProgress sx={{ mt: 1 }} />}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={4}
                          sx={{ display: "flex", justifyContent: "flex-end", columnGap: 1 }}
                        >
                          <CircularProgress sx={{ mt: 1 }} />
                        </Grid>
                      </Grid>
                    </ProtoListItem>
                  ))}
                </List>
              </CardContent>
            </ProtoCard>
          </Box>
        </Box>
      </Box>
    );
  }

  // Render field item
  const renderFieldItem = (fieldName) => {
    const fieldLabelMapping = {
      alias: t("alias"),
      lat: t("lat"),
      lng: t("lng"),
    };
    const label = fieldLabelMapping[fieldName];
    const isEditing = editMode.field === fieldName;

    return (
      <ProtoListItem key={fieldName} sx={{ py: 1 }}>
        <Grid container alignItems='center'>
          <Grid item xs={8}>
            <Box display='flex' gap={2} alignItems={"center"}>
              <ListItemText
                sx={{ width: { lg: "14em" } }}
                primary={label}
                secondary={isEditing ? "" : project[fieldName]}
              />
              {isEditing && (
                <TextField
                  margin='dense'
                  required
                  fullWidth
                  id={fieldName}
                  name={fieldName}
                  autoComplete={fieldName}
                  autoFocus
                  value={editMode.value}
                  onChange={(e) => setEditMode({ ...editMode, value: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title={t("cancel")}>
                          <IconButton size='small' color='error' onClick={handleCancelEdit}>
                            <Cancel fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("save")}>
                          <IconButton
                            size='small'
                            color='primary'
                            onClick={() => handleEditToggle(fieldName)}
                          >
                            <Save fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ),
                  }}
                />
              )}
            </Box>
          </Grid>
          <Grid item xs={4} sx={{ display: "flex", justifyContent: "flex-end", columnGap: 1 }}>
            {!isEditing && (
              <Button
                variant='outlined'
                color='primary'
                onClick={() => handleEditToggle(fieldName)}
                size='small'
                startIcon={<Edit fontSize='small' />}
              >
                {t("edit")}
              </Button>
            )}
          </Grid>
        </Grid>
      </ProtoListItem>
    );
  };

  return (
    <Box sx={{ height: "100%", p: 1 }}>
      {/* 使用CSS Grid实现等高布局 */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 1,
          height: "100%",
          // md断点以上使用两列布局
          "@media (min-width: 900px)": {
            gridTemplateColumns: "5fr 7fr",
          },
        }}
      >
        {/* 左侧卡片 - 项目信息 */}
        <Box sx={{ height: "100%" }}>
          <ProtoCard sx={{ height: "100%", boxShadow: 2, overflow: "hidden" }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
                <Avatar sx={{ width: 80, height: 80 }}>
                  <SwalMediaImageUpload
                    props={{
                      component: "img",
                      isWithBlur: false,
                      title: project.alias ?? "(沒有名字)",
                      height: "150",
                      width: "150",
                      style: {
                        objectFit: "cover",
                        objectPosition: "0 -50px",
                        borderRadius: "4px 4px 0 0",
                      },
                      src: base64ToImg({ base64String: project.img }),
                      alt: "Card Background",
                    }}
                    onUpload={(file) => handleImageUpload(file)}
                  />
                </Avatar>
                <Typography variant='h6' gutterBottom>
                  {project.alias}
                </Typography>
                <Typography variant='body2' color='text.secondary' gutterBottom>
                  ID: {project.id}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <List sx={{ p: 0 }}>
                <ProtoListItem sx={{ py: 1 }}>
                  <ListItemText primary={t("status")} secondary={project.status} />
                </ProtoListItem>
                <ProtoListItem sx={{ py: 1 }}>
                  <ListItemText
                    primary={t("createdat")}
                    secondary={dayjs(project.created_at).format("YYYY-MM-DD HH:mm:ss")}
                  />
                </ProtoListItem>
                <ProtoListItem sx={{ py: 1 }}>
                  <ListItemText
                    primary={t("updatedat")}
                    secondary={dayjs(project.updated_at).format("YYYY-MM-DD HH:mm:ss")}
                  />
                </ProtoListItem>
              </List>
            </CardContent>
          </ProtoCard>
        </Box>

        {/* 右侧卡片 - 项目详细信息 */}
        <Box sx={{ height: "100%" }}>
          <ProtoCard sx={{ height: "100%", boxShadow: 2, overflow: "hidden" }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant={"h6"} sx={{ mb: 1 }}>
                {t("projectinformation")}
              </Typography>
              <List sx={{ p: 0 }}>
                {["alias", "lat", "lng"].map((fieldName) => renderFieldItem(fieldName))}
              </List>
            </CardContent>
          </ProtoCard>
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectDetails;
